import React, { useState, useEffect, useMemo } from 'react';
import { Steps, Button, message } from 'antd';
import { UserOutlined, BookOutlined, FileTextOutlined, PaperClipOutlined, SaveOutlined } from '@ant-design/icons';
import StudentProfileForm from './StudentProfileForm';
import { SUBJECTS, GRADES } from '../../../config';
import './StudentProfileSteps.css';
import './StudentProfileFormMobile.css';

const { Step } = Steps;

const StudentProfileFormSteps = ({
    form,
    subjects,
    periods,
    gradeFields,
    submitLoading,
    onFinish,
    initialValues,
    uploadProps,
    uploadLoading,
    uploadProgress,
    uploadStatus,
    fileList,
    attachmentTypes,
    subjectTypes,
    studentProfileData,
    onFileSelect,
    onManualUpload,
    onBatchUpload
}) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [completedSteps, setCompletedSteps] = useState([]);
    const [attachmentErrors, setAttachmentErrors] = useState({});

    // Generate all grade field names (e.g. math_10, math_11, math_12, ...)
    const gradeFieldNames = useMemo(() => {
        const names = [];
        Object.keys(SUBJECTS).forEach(subject => {
            GRADES.forEach(grade => {
                names.push(`${subject}_${grade}`);
            });
        });
        return names;
    }, []);

    // Define which fields belong to which step
    const stepFields = useMemo(() => ({
        0: [ // Bước 1: Thông tin cá nhân + Năm tốt nghiệp + Khu vực + Đối tượng
            'full_name',
            'date_of_birth',
            'identification',
            'identification_verify',
            'student_phone_number',
            'parents_phone_number',
            'province_id',
            'commune_id',
            'contact_address',
            'graduation_year',
            'admission_area',
            'admission_group'
        ],
        1: [ // Bước 2: IELTS + Thành tích
            'ielts_grade',
            'provincial_prize',
            'provincial_prize_subject_id',
            'provincial_prize_grade',
            'grade_10_rank',
            'grade_11_rank',
            'grade_12_rank',
            'is_HSA',
            'is_TSA',
            'is_SPT'
        ],
        2: [ // Bước 3: Nguyện vọng & Điểm
            'first_admission_subject_type_id',
            'first_admission_id',
            ...gradeFieldNames,
        ],
        3: [] // Bước 4: Minh chứng (validated separately)
    }), [gradeFieldNames]);

    const steps = [
        {
            title: 'Bước 1',
            icon: <UserOutlined />,
            description: 'Thông tin cá nhân'
        },
        {
            title: 'Bước 2',
            icon: <BookOutlined />,
            description: 'IELTS & Thành tích'
        },
        {
            title: 'Bước 3',
            icon: <FileTextOutlined />,
            description: 'Nguyện vọng & Điểm thi'
        },
        {
            title: 'Bước 4',
            icon: <PaperClipOutlined />,
            description: 'Minh chứng'
        }
    ];

    // Helper function to check if attachment is required
    // (Cũ: cứng trong frontend theo typeCode và graduation_year)
    const isAttachmentRequired = (type, formValues) => {
        // IELTS: conditional — bắt buộc khi đã nhập điểm IELTS
        if (type.code === 'IELTS') {
            return !!(formValues?.ielts_grade && formValues.ielts_grade !== '');
        }
        // PRIORITY: conditional — bắt buộc khi đã điền ĐẦY ĐỦ thông tin giải tỉnh
        if (type.code === 'PRIORITY') {
            return !!(formValues?.provincial_prize &&
                   formValues.provincial_prize !== '' &&
                   formValues?.provincial_prize_subject_id &&
                   formValues.provincial_prize_subject_id !== '' &&
                   formValues?.provincial_prize_grade &&
                   formValues.provincial_prize_grade !== '');
        }
        // Các loại còn lại: dùng cờ is_required từ backend
        // (Cũ: return typeCode === 'FRONT_ID_CARD' || typeCode === 'BACK_ID_CARD')
        // (Cũ: TRANSCRIPT chỉ bắt buộc khi graduation_year < 2026)
        return !!type.is_required;
    };

    // Validate attachments and return errors object
    const validateAttachments = () => {
        const formValues = form.getFieldsValue();
        const errors = {};

        attachmentTypes.forEach(type => {
            if (isAttachmentRequired(type, formValues)) {
                const hasFile = fileList[type.id] && fileList[type.id].length > 0;
                if (!hasFile) {
                    errors[type.id] = `Vui lòng tải lên ${type.name}`;
                }
            }
        });

        return errors;
    };

    // Validate current step before moving to next
    const validateCurrentStep = async () => {
        try {
            // For step 3 (attachments), use custom validation
            if (currentStep === 3) {
                const errors = validateAttachments();
                if (Object.keys(errors).length > 0) {
                    setAttachmentErrors(errors);
                    // Get first error message
                    const firstError = Object.values(errors)[0];
                    message.error(firstError);
                    // Scroll to attachments section
                    const attachmentSection = document.getElementById('attachments-section');
                    if (attachmentSection) {
                        attachmentSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                    return false;
                }
                // Clear errors if validation passes
                setAttachmentErrors({});
                return true;
            }

            // For other steps, validate their fields
            const fieldsToValidate = stepFields[currentStep];
            
            if (fieldsToValidate && fieldsToValidate.length > 0) {
                await form.validateFields(fieldsToValidate);
            }

            // Additional validation for step 0 (ensure province and commune are selected)
            if (currentStep === 0) {
                const provinceId = form.getFieldValue('province_id');
                const communeId = form.getFieldValue('commune_id');
                
                if (!provinceId) {
                    message.error('Vui lòng chọn Tỉnh/Thành phố!');
                    return false;
                }
                
                if (!communeId) {
                    message.error('Vui lòng chọn Xã/Phường!');
                    return false;
                }
            }

            // Additional validation for step 2 (grades and admissions)
            if (currentStep === 2) {
                // Validate first admission is selected
                const firstAdmission = form.getFieldValue('first_admission_id');
                if (!firstAdmission) {
                    message.error('Vui lòng chọn nguyện vọng 1!');
                    return false;
                }
            }

            return true;
        } catch (errorInfo) {
            // Scroll to first error field
            if (errorInfo.errorFields && errorInfo.errorFields.length > 0) {
                const firstErrorField = errorInfo.errorFields[0].name;
                form.scrollToField(firstErrorField, {
                    behavior: 'smooth',
                    block: 'center'
                });
                message.error(errorInfo.errorFields[0].errors[0]);
            }
            return false;
        }
    };

    // Handle next step
    const handleNext = async () => {
        const isValid = await validateCurrentStep();
        if (isValid) {
            // Mark current step as completed
            if (!completedSteps.includes(currentStep)) {
                setCompletedSteps([...completedSteps, currentStep]);
            }
            setCurrentStep(currentStep + 1);
            // Scroll to top
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    // Handle previous step
    const handlePrev = () => {
        setCurrentStep(currentStep - 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Handle form submission
    const handleSubmit = async () => {
        const isValid = await validateCurrentStep();
        if (isValid) {
            // Validate all fields one more time
            try {
                const values = await form.validateFields();
                
                // Final check for attachments
                const errors = validateAttachments();
                if (Object.keys(errors).length > 0) {
                    setAttachmentErrors(errors);
                    setCurrentStep(3); // Go back to attachments step
                    message.error('Vui lòng tải lên đầy đủ minh chứng bắt buộc');
                    return;
                }
                
                onFinish(values);
            } catch (errorInfo) {
                // Find which step has the error
                for (let stepIndex = 0; stepIndex < 4; stepIndex++) {
                    const stepFieldsList = stepFields[stepIndex];
                    const hasError = errorInfo.errorFields?.some(error => 
                        stepFieldsList.includes(error.name[0])
                    );
                    if (hasError) {
                        setCurrentStep(stepIndex);
                        message.error(`Vui lòng kiểm tra lại thông tin ở bước ${stepIndex + 1}`);
                        break;
                    }
                }
            }
        }
    };

    // Clear attachment errors when files are uploaded
    useEffect(() => {
        if (Object.keys(attachmentErrors).length > 0) {
            const updatedErrors = { ...attachmentErrors };
            let hasChanges = false;
            
            Object.keys(updatedErrors).forEach(typeId => {
                if (fileList[typeId] && fileList[typeId].length > 0) {
                    delete updatedErrors[typeId];
                    hasChanges = true;
                }
            });
            
            if (hasChanges) {
                setAttachmentErrors(updatedErrors);
            }
        }
    }, [fileList, attachmentErrors]);

    // Calculate step status
    const getStepStatus = (stepIndex) => {
        if (stepIndex === currentStep) return 'process';
        if (completedSteps.includes(stepIndex)) return 'finish';
        return 'wait';
    };

    return (
        <div className="student-profile-steps-container">
            {/* Progress Steps */}
            <div className="steps-header">
                <Steps 
                    current={currentStep}
                    direction="horizontal"
                    onChange={(step) => {
                        // Allow jumping to previous steps or completed steps
                        if (step < currentStep || completedSteps.includes(step)) {
                            setCurrentStep(step);
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                        }
                    }}
                    className="custom-steps"
                >
                    {steps.map((step, index) => (
                        <Step
                            key={index}
                            title={step.title}
                            description={step.description}
                            icon={step.icon}
                            status={getStepStatus(index)}
                        />
                    ))}
                </Steps>
            </div>

            {/* Form Content - Pass current step to control visibility */}
            <div className="steps-content">
                <StudentProfileForm
                    form={form}
                    subjects={subjects}
                    periods={periods}
                    gradeFields={gradeFields}
                    submitLoading={submitLoading}
                    onFinish={onFinish}
                    initialValues={initialValues}
                    uploadProps={uploadProps}
                    uploadLoading={uploadLoading}
                    uploadProgress={uploadProgress}
                    uploadStatus={uploadStatus}
                    fileList={fileList}
                    attachmentTypes={attachmentTypes}
                    subjectTypes={subjectTypes}
                    studentProfileData={studentProfileData}
                    onFileSelect={onFileSelect}
                    onManualUpload={onManualUpload}
                    onBatchUpload={onBatchUpload}
                    currentStep={currentStep}
                    externalAttachmentErrors={attachmentErrors}
                />
            </div>

            {/* Navigation Buttons */}
            <div className="steps-action">
                <div className="button-group">
                    {currentStep > 0 && (
                        <Button 
                            size="large"
                            onClick={handlePrev}
                            disabled={submitLoading}
                        >
                            Quay lại
                        </Button>
                    )}
                    
                    {currentStep < steps.length - 1 && (
                        <Button 
                            type="primary" 
                            size="large"
                            onClick={handleNext}
                            disabled={submitLoading}
                        >
                            Tiếp tục
                        </Button>
                    )}
                    
                    {currentStep === steps.length - 1 && (
                        <Button 
                            type="primary" 
                            size="large"
                            icon={<SaveOutlined />}
                            onClick={handleSubmit}
                            loading={submitLoading}
                        >
                            {submitLoading ? 'Đang xử lý...' : 'Hoàn tất và lưu'}
                        </Button>
                    )}
                </div>
                
                {/* Progress indicator */}
                <div className="progress-text">
                    Bước {currentStep + 1} / {steps.length}
                </div>
            </div>
        </div>
    );
};

export default StudentProfileFormSteps;
